import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { StructureAddressDto } from '../../../../shared/models/structure-address.dto';
import { StructureDto } from '../../../../shared/models/structure.dto';
import { Utils } from '../../../../shared/utils';

@Component({
  selector: 'app-structure-address',
  templateUrl: './structure-address.component.html',
  standalone: false,
})
export class StructureAddressComponent implements OnInit {
  public mainAddressForm: FormGroup<{
    id: FormControl<number>;
    address: FormControl<{ properties: { name: string } }>;
    additionalAddress: FormControl<string>;
    postalCode: FormControl<string>;
    city: FormControl<string>;
    phone: FormControl<string>;
    main: FormControl<boolean>;
    name: FormControl<string>;
    active: FormControl<boolean>;
    publicReception: FormControl<boolean>;
    geoJSON: FormControl<any>;
  }>;

  public secondaryAddressesForm: FormGroup<{
    id: FormControl<number | null>;
    address: FormControl<any | null>;
    additionalAddress: FormControl<string | null>;
    postalCode: FormControl<string | null>;
    city: FormControl<string | null>;
    phone: FormControl<string | null>;
    name: FormControl<string | null>;
    active: FormControl<boolean>;
    publicReception: FormControl<boolean>;
    main: FormControl<boolean>;
    geoJSON: FormControl<any>;
  }>;

  @Input() public structure: StructureDto;

  public submitted: boolean = false;
  public secondarySubmitted: boolean = false;
  public secondaryIndex: number | null = null;

  public mainAddress: StructureAddressDto;
  public secondaryAddresses: StructureAddressDto[] = [];

  constructor(private readonly formBuilder: FormBuilder) {}

  public async ngOnInit(): Promise<void> {
    this.mainAddress = this.structure.addresses.filter((addr) => addr.main)[0] ?? new StructureAddressDto();
    this.secondaryAddresses = this.structure.addresses
      .filter((addr) => !addr.main)
      .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
    this.loadForm();
  }

  public onSubmit(): boolean {
    this.submitted = true;

    if (this.mainAddressForm.valid) {
      Object.assign(this.mainAddress, this.mainAddressForm.value, {
        address: this.mainAddressForm.controls.address.value?.properties.name,
        city: this.mainAddressForm.controls.city.value,
        postalCode: this.mainAddressForm.controls.postalCode.value,
        geoJSON: this.mainAddressForm.controls.geoJSON.value,
      });

      Object.assign(this.structure.addresses, [].concat(this.mainAddress, this.secondaryAddresses));
    }

    return this.mainAddressForm.valid;
  }

  private loadForm(): void {
    this.mainAddressForm = this.formBuilder.group({
      id: new FormControl<number | null>(this.mainAddress?.id),
      address: new FormControl<any | null>(this.mainAddress?.address ? { properties: { name: this.mainAddress.address } } : undefined, [
        Validators.required,
      ]),
      additionalAddress: new FormControl<string | null>(this.mainAddress?.additionalAddress, []),
      postalCode: new FormControl<string | null>({ value: this.mainAddress?.postalCode, disabled: true }, [
        Validators.pattern(Utils.postalCodePattern),
      ]),
      city: new FormControl<string | null>({ value: this.mainAddress?.city, disabled: true }, [Validators.pattern(Utils.alphabeticalPattern)]),
      phone: new FormControl<string | null>(this.mainAddress?.phone, [Validators.pattern(Utils.phonePattern)]),
      publicReception: new FormControl<boolean | null>(this.mainAddress?.publicReception ?? true, []),
      name: new FormControl<string | null>('Siège', []),
      active: new FormControl<boolean | null>(true, []),
      main: new FormControl<boolean | null>(true, []),
      geoJSON: new FormControl<any | null>(this.mainAddress?.geoJSON, []),
    });

    this.secondaryAddressesForm = this.formBuilder.group({
      id: new FormControl<number | null>(null),
      address: new FormControl<any | null>(null, [Validators.required]),
      additionalAddress: new FormControl<string | null>(null),
      postalCode: new FormControl<string | null>({ value: null, disabled: true }, [Validators.pattern(Utils.postalCodePattern)]),
      city: new FormControl<string | null>({ value: null, disabled: true }, [Validators.pattern(Utils.alphabeticalPattern)]),
      phone: new FormControl<string | null>(null, [Validators.pattern(Utils.phonePattern)]),
      name: new FormControl<string | null>(null, [Validators.required]),
      publicReception: new FormControl<boolean>(true),
      active: new FormControl<boolean>(true),
      main: new FormControl<boolean>(false),
      geoJSON: new FormControl<any | null>(null, []),
    });
  }

  public loadAddressByIndex(index: number) {
    this.secondaryIndex = index;
    const address: StructureAddressDto = this.secondaryAddresses[index];

    this.secondaryAddressesForm.controls.id.setValue(address.id);
    this.secondaryAddressesForm.controls.address.setValue({ properties: { name: address.address } });
    this.secondaryAddressesForm.controls.additionalAddress.setValue(address.additionalAddress);
    this.secondaryAddressesForm.controls.postalCode.setValue(address.postalCode);
    this.secondaryAddressesForm.controls.city.setValue(address.city);
    this.secondaryAddressesForm.controls.phone.setValue(address.phone);
    this.secondaryAddressesForm.controls.name.setValue(address.name);
    this.secondaryAddressesForm.controls.active.setValue(address.active);
    this.secondaryAddressesForm.controls.publicReception.setValue(address.publicReception);
    this.secondaryAddressesForm.controls.main.setValue(false);
    this.secondaryAddressesForm.controls.geoJSON.setValue(address.geoJSON);
  }

  public resetSecondaryForm(): void {
    this.secondaryAddressesForm.reset();
    this.secondaryAddressesForm.controls.active.setValue(true);
    this.secondaryAddressesForm.controls.publicReception.setValue(true);
    this.secondaryAddressesForm.controls.main.setValue(false);

    this.secondarySubmitted = false;
    this.secondaryIndex = null;
  }

  public updateSecondaryAddress() {
    this.secondarySubmitted = true;
    if (this.secondaryAddressesForm.valid) {
      if (this.secondaryIndex !== null) {
        const secondaryAddress: StructureAddressDto = Object.assign(this.secondaryAddressesForm.value, {
          address: this.secondaryAddressesForm.controls.address.value?.properties.name,
          city: this.secondaryAddressesForm.controls.city.value,
          postalCode: this.secondaryAddressesForm.controls.postalCode.value,
          geoJSON: this.secondaryAddressesForm.controls.geoJSON.value,
        }) as StructureAddressDto;
        Object.assign(this.secondaryAddresses[this.secondaryIndex], secondaryAddress) as StructureAddressDto;
      } else {
        const address: StructureAddressDto = {} as StructureAddressDto;
        Object.assign(address, this.secondaryAddressesForm.value, {
          address: this.secondaryAddressesForm.controls.address.value?.properties.name,
          city: this.secondaryAddressesForm.controls.city.value,
          postalCode: this.secondaryAddressesForm.controls.postalCode.value,
          geoJSON: this.secondaryAddressesForm.controls.geoJSON.value,
          id: null,
        });
        this.structure.addresses.push(address);
        this.secondaryAddresses = this.structure.addresses
          .filter((addr) => !addr.main)
          .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
      }
      this.resetSecondaryForm();
    }
  }
}
